import { IbanElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import { Text } from 'hb-react/shared/components/formik/components/inputs';
import { Errors } from 'hb-react/shared/components/Payment/components/Stripe/Errors';
import { STRIPE_PAYMENT_TYPES } from 'hb-react/shared/components/Payment/constants';
import { usePaymentContext } from 'hb-react/shared/components/Payment/context/PaymentContext';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import MandateText from '../MandateText';

const BancontactForm = () => {
  const {
    paymentAccountConfig,
    paymentEndpoint,
    paymentData,
    billingData,
    amountFormatted,
    isLoading,
    setLoading,
    returnUrl,
    hasRecurringItems,
  } = usePaymentContext();

  const [errors, setErrors] = useState(null);

  const stripe = useStripe();
  const elements = useElements();

  const { type: gateway } = paymentAccountConfig;

  const confirmPaymentIntent = (clientSecret, name, email) =>
    stripe.confirmBancontactPayment(clientSecret, {
      payment_method: {
        billing_details: {
          name,
          ...(hasRecurringItems ? { email } : {}),
        },
      },
      return_url: returnUrl,
    });

  const setupPayment = () =>
    axios.post(paymentEndpoint, {
      ...paymentData,
      billingData,
      billing: {
        payment_method: STRIPE_PAYMENT_TYPES.BANCONTACT,
        gateway,
      },
    });

  const handleSubmit = async ({ accountholderName, accountholderEmail }) => {
    setLoading(true);
    try {
      const {
        data: { client_secret: clientSecret, is_setup: isSetup },
      } = await setupPayment();

      const iban = elements.getElement(IbanElement);

      if (isSetup) {
        const result = await stripe.confirmBancontactSetup(clientSecret, {
          payment_method: {
            sepa_debit: iban,
            billing_details: {
              name: accountholderName,
              email: accountholderEmail,
            },
          },
          return_url: returnUrl,
        });

        if (result.error) {
          displayNotif(
            'error',
            s__(
              'StripeBancontact|An error occurred saving the payment details',
            ),
          );
          errorReporting.captureException(result.error);
        }
      } else {
        await confirmPaymentIntent(
          clientSecret,
          accountholderName,
          accountholderEmail,
        );
      }
    } catch (error) {
      setErrors(error?.response?.data?.errors);
      displayNotif(
        'error',
        s__('StripeBancontact|An error occurred processing the payment'),
      );
      errorReporting.captureException(error);
    }

    setLoading(false);
  };

  const initialValues = {
    accountholderName: billingData?.name || '',
    accountholderEmail: billingData?.email || '',
  };

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues}>
      {() => (
        <Form>
          {errors && <Errors errors={errors} />}
          <Wrapper>
            <Text
              name="accountholderName"
              label={s__('StripeBancontact|Accountholder Name')}
              validate={{
                required: true,
              }}
            />
            {hasRecurringItems && (
              <>
                <Text
                  name="accountholderEmail"
                  label={s__('StripeBancontact|Accountholder Email')}
                  validate={{
                    required: true,
                  }}
                />
                <MandateText
                  merchantDisplayName={
                    paymentAccountConfig.merchant_display_name
                  }
                />
              </>
            )}
            <Box
              justifyContent="space-between"
              alignContent="center"
              width="100%"
              mt={3}
            >
              <Box ml="auto" pl={3}>
                <Button
                  data-testid="stripe-sepa-debit-form-submit"
                  type="submit"
                  isLoading={isLoading}
                >
                  {__('Pay %{amountFormatted}').i__({
                    amountFormatted,
                  })}
                </Button>
              </Box>
            </Box>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

export default BancontactForm;

const Wrapper = styled(Box)`
  flex-direction: column;
  row-gap: 12px;
`;
